// StepSelectProduct.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Collapse,
  Paper,
  FormControl,
} from "@mui/material";
import { useSelector } from "react-redux";
import Label from "../../../components/Label/Label";

const StepSelectProduct = ({ formData = {}, setFormData }) => {
  const { loader = false, data = [] } = useSelector((state) => {
    const productsAndSitesList = state?.viz?.productsAndSitesList;
    return {
      loader: productsAndSitesList?.loader ?? false,
      data: Array.isArray(productsAndSitesList?.data)
        ? productsAndSitesList.data
        : [],
    };
  });

  const selectedProductId = formData?.product?.product_id || "";
  const selectedSiteId = formData?.product?.site_id || "";

  const [expandedProduct, setExpandedProduct] = useState(selectedProductId);

  // keep expandedProduct in sync if formData changes externally
  useEffect(() => {
    if (selectedProductId) setExpandedProduct(selectedProductId);
  }, [selectedProductId]);

  const handleProductChange = useCallback(
    (productId) => {
      const product = data.find((p) => p.product_id === productId);
      if (!product) return;

      setExpandedProduct(product.product_id);

      setFormData((prev = {}) => ({
        ...prev,
        product: {
          product_id: product.product_id,
          product_name: product.product_name,
          site_id: "", // reset site when product changes
          site_name: "",
        },
      }));
    },
    [data, setFormData]
  );

  const handleSiteChange = useCallback(
    (siteId, productId) => {
      const product = data.find((p) => p.product_id === productId);
      const site = product?.sites?.find((s) => s.site_id === siteId);

      setFormData((prev = {}) => ({
        ...prev,
        product: {
          product_id: product.product_id,
          product_name: product.product_name,
          site_id: site?.site_id || "",
          site_name: site?.site_name || "",
        },
      }));
    },
    [data, setFormData]
  );

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <Box>
        <Label text="No products available." fontSize="14px" fontWeight="600" />
      </Box>
    );
  }

  return (
    <Box>
      <Label text="Select Product" fontSize="14px" fontWeight="700" />

      <FormControl component="fieldset" fullWidth sx={{ pt: 1 }}>
        <RadioGroup
          name="products"
          value={selectedProductId}
          onChange={(e) => handleProductChange(e.target.value)}
        >
          {data.map((product) => {
            const isExpanded = expandedProduct === product.product_id;
            const siteGroupValue = isExpanded ? selectedSiteId : "";

            return (
              <Box key={product.product_id} mb={1}>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <FormControlLabel
                    value={product.product_id}
                    control={<Radio />}
                    label={product.product_name}
                    sx={{ ml: 1 }}
                  />

                  {/* Sites radio group */}
                  <Collapse in={isExpanded}>
                    <Box sx={{ pl: 4, pt: 1 }}>
                      <Label
                        text="Select Site"
                        fontSize="12px"
                        fontWeight="500"
                      />

                      {Array.isArray(product.sites) &&
                      product.sites.length > 0 ? (
                        <RadioGroup
                          name={`sites-${product.product_id}`}
                          value={siteGroupValue}
                          onChange={(e) =>
                            handleSiteChange(e.target.value, product.product_id)
                          }
                        >
                          {product.sites.map((site) => (
                            <FormControlLabel
                              key={site.site_id}
                              value={site.site_id}
                              control={<Radio />}
                              label={site.site_name}
                              sx={{ display: "block", pl: 1 }}
                            />
                          ))}
                        </RadioGroup>
                      ) : (
                        <Label
                          text="No sites available"
                          fontSize="12px"
                          fontWeight="400"
                          color="gray"
                        />
                      )}
                    </Box>
                  </Collapse>
                </Paper>
              </Box>
            );
          })}
        </RadioGroup>
      </FormControl>
    </Box>
  );
};

export default StepSelectProduct;
